import { DatePipe } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";

import { faListAlt } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AccountManager } from "../../model/domain/AccountManager";
import { Group } from "../../model/domain/Group";
import { PagedList } from "../../model/domain/PagedList";
import { User } from "../../model/domain/User";
import { AuthService } from "../../services/auth.service";
import { DownloadService } from "../../services/download.service";
import { LoaderService } from "../../services/loader.service";
import { SecurityService } from "../../services/security.service";
import { UserService } from "../../services/user.service";
import { Roles } from "../auth/auth.role";

@Component({
  selector: "app-usuarios",
  templateUrl: "./usuarios.component.html",
  styleUrls: ["./usuarios.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class UsuariosComponent implements OnInit {
  faListAlt = faListAlt;

  displayedColumns = [
    "actions",
    "name",
    "email",
    "userName",
    "groupNames",
    "passwordExpirationDate",
    "active",
    "lockoutEnabled",
  ];

  model: User[] = null;
  accountManagers: AccountManager[] = null;
  selectedAccManager: AccountManager = null;
  selectedGroup: Group = null;
  isExternalUser = true;

  currentPage: number = 1;
  totalPages: number = null;
  totalItems: number = null;
  listedPages: any[];
  firstItemOnPage: number;
  lastItemOnPage: number;

  nomeUsuario: string = null;

  groupList: Group[] = null;

  constructor(
    private service: UserService,
    private router: Router,
    private datePipe: DatePipe,
    private securityService: SecurityService,
    private authService: AuthService,
    private downloadService: DownloadService,
    private loaderService: LoaderService
  ) {}

  ngOnInit() {
    if (this.canCreateMaster()) {
      this.securityService.GetAccountManagers().subscribe((a) => {
        this.accountManagers = a;
      });

      this.getUsers(1, 10);
    } else if (this.canCreateSubordinate()) {
      this.securityService
        .GetAccountManager(this.authService.authToken.username)
        .subscribe((g) => {
          if (g != null) {
            this.selectedAccManager = g;
            this.getUsers(1, 10);
          } else {
            this.getUsers(1, 10);
          }
        });
    }

    this.getGroups();
  }

  getUsers(page: number = null, pageSize: number = null) {
    if (!this.isExternalUser) {
      this.selectedAccManager = null;
    }

    if (!page) {
      page = 1;
    }

    if (!pageSize) {
      pageSize = 10;
    }

    this.service
      .GetUsers(
        this.selectedAccManager?.id,
        this.isExternalUser,
        this.selectedGroup?.id,
        this.nomeUsuario,
        page,
        pageSize
      )
      .subscribe((q) => {
        this.model = q.results;
        this.model.forEach((item) => {
          item.passwordExpirationDate = this.datePipe.transform(
            item.passwordExpirationDate,
            "dd/MM/yyyy"
          ) as any;
        });
        this.renderPaginador(q, page);
      });
  }

  clearFilters() {
    this.service.GetUsers(null, true, null, null, 1, 10).subscribe((q) => {
      this.model = q.results;
      this.renderPaginador(q, 1);
    });

    this.isExternalUser = true;
    this.nomeUsuario = null;
    this.selectedGroup = null;
    this.selectedAccManager = null;
  }

  renderPaginador(result: PagedList<User>, page: number) {
    this.totalItems = result.rowCount;
    this.totalPages = result.pageCount;
    this.firstItemOnPage = result.firstRowOnPage;
    this.lastItemOnPage = result.lastRowOnPage;

    this.currentPage = page;

    this.listedPages = [];

    let startPage = this.currentPage - 2;

    if (startPage <= 0) {
      startPage = 1;
    }

    let maxPage = this.currentPage + 2;

    while (maxPage > this.totalPages) {
      maxPage--;
    }

    for (let i = startPage; i <= maxPage; i++) {
      this.listedPages.push(i);
    }

    if (!result.results.length) {
      this.totalPages = 1;
      this.currentPage = 1;
      this.firstItemOnPage = 0;

      this.listedPages = [];
      this.listedPages.push(1);
    }
  }

  openUser(id: number = null) {
    if (id === null) {
      this.router.navigate(["/cadastro-usuario"]);
    } else {
      this.router.navigate(["/cadastro-usuario", id]);
    }
  }

  canCreateMaster() {
    const roles = [Roles.CreateMasterUser];
    return this.authService.hasRoles(roles);
  }

  canCreateSubordinate() {
    const roles = [Roles.CreateSubordinateUser];
    return this.authService.hasRoles(roles);
  }

  exportUsers() {
    this.loaderService.addLoader();
    const method = this.service.ExportUsers(
      this.selectedAccManager?.id,
      this.isExternalUser
    );

    return method
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          return throwError("Erro ao exportar.");
        })
      )
      .subscribe((data: Blob) => {
        const filename = `usuarios_${DateTime.local().toFormat(
          "yyyyMMdd"
        )}.xlsx`;
        this.downloadService.downloadFile(data, filename);

        this.loaderService.removeLoader();
      });
  }

  getGroups() {
    this.securityService.GetGroups().subscribe((groups) => {
      this.groupList = groups;
    });
  }

  getAccountManagerName(item?: any): string | undefined {
    return item ? item.document + " " + item.name : undefined;
  }

  getGroupName(item?: any): string | undefined {
    return item ? item.name : undefined;
  }
}
